import {
  useCreateGuestOrderMutation,
  useCreateOrderMutation,
} from "@/redux/apiSlices/orderApis";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import usePaypalPayment from "./usePaypalPayment";
import useAuthorizePayment from "./useAuthorizePayment";
import useAffirmCheckout from "./useAffirmCheckout";
import useSynchronyCheckout from "./useSynchronyCheckout";
import { useToast } from "@/components/ui/use-toast";
import { setGuestToken } from "@/lib/auth";
import { setBearerToken } from "@/redux/slices/authSlice";

const useCreateOrder = (paymentMethod, priceDetails, orderDetails) => {
  const isLogin = useSelector((state) => state.auth.isLogin);
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();
  const dispatch = useDispatch();
  // console.log("priceDetails==",priceDetails,"===orderDetails===",orderDetails)

  const [
    createOrder,
    {
      data: orderRes,
      isSuccess: isOrderSuccess,
      isLoading: isOrderLoading,
      isError: isOrderError,
      error: orderError,
    },
  ] = useCreateOrderMutation();

  const [
    createGuestOrder,
    {
      data: guestOrderRes,
      isSuccess: isGuestOrderSuccess,
      isLoading: isGuestOrderLoading,
      isError: isGuestOrderError,
      error: guestOrderError,
    },
  ] = useCreateGuestOrderMutation();
  const { orderId, token } =
    (isOrderSuccess ? orderRes?.data : guestOrderRes?.data) || {};
  const { paypalPaymentHandler, isPaymentLoading } = usePaypalPayment();
  // const { authorizePaymentHandler, isAuthorizeLoading } = useAuthorizePayment(orderId);
  const { authorizePaymentHandler } = useAuthorizePayment(
    orderId,
    setIsProcessing
  );
  const { affirmCheckoutHandler } = useAffirmCheckout(
    priceDetails,
    orderDetails,
    setIsProcessing,
    orderId
  );

  const { synchronyCheckoutHandler } = useSynchronyCheckout();

  useEffect(() => {
    if (token) {
      dispatch(setBearerToken({ guestToken: token, isLogin: false }));
      setGuestToken(token);
    }
  }, [token]);

  useEffect(() => {
    const handlePayment = async () => {
      if (!orderId) return;

      // hasCalledRef.current = true;
      setIsProcessing(true);
      try {
        if (paymentMethod === "paypal") {
          await paypalPaymentHandler(orderId);
          setIsProcessing(false);
        } else if (paymentMethod === "authorize") {
          await authorizePaymentHandler(orderId);
        } else if (paymentMethod === "syncrony") {
          await synchronyCheckoutHandler(
            priceDetails,
            orderDetails,
            setIsProcessing,
            orderId
          );
        } else if (paymentMethod === "affirm") {
          await affirmCheckoutHandler(orderId);
          setIsProcessing(false);
        }
      } catch (error) {
        console.error("Payment handler error:", error);
      }
    };
    if (isOrderSuccess || isGuestOrderSuccess) {
      handlePayment();
    }
  }, [
    isOrderSuccess,
    isGuestOrderSuccess,
    orderId,
    paymentMethod,
    // paypalPaymentHandler,
    // authorizePaymentHandler,
    // synchronyCheckoutHandler,
    // affirmCheckoutHandler,
    // isProcessing,
    // setIsProcessing,
  ]);

  useEffect(() => {
    if (isOrderError || isGuestOrderError) {
      const errMsg = isOrderError ? orderError : guestOrderError || null;
      if (errMsg) {
        toast({
          variant: "destructive",
          title: errMsg?.data?.msg,
        });
      }

      setIsProcessing(false);
    }
  }, [
    isOrderError,
    isGuestOrderError,
    orderError,
    guestOrderError,
    toast,
    setIsProcessing,
  ]);

  const createOrderHandler = (payload, extraParams) => {
    const {
      shippingAddressId,
      billingAddressId,
      email,
      address,
      billingAddress,
      termsAccepted,
    } = extraParams;
    if (isLogin) {
      createOrder({
        ...payload,
        shippingAddressId,
        billingAddressId,
        termsAccepted: false,
      });
    } else {
      createGuestOrder({
        ...payload,
        email,
        address,
        billingAddress,
        termsAccepted,
      });
    }
  };
  return {
    createOrder: createOrderHandler,
    isOrderLoading:
      isOrderLoading || isGuestOrderLoading || isPaymentLoading || isProcessing,
  };
};

export default useCreateOrder;
